import { useCallback, useEffect, useMemo, useState } from "react";
import { FileSearch, Layers, Plus, Search } from "lucide-react";
import { ReactComponent as AggIcon } from "../../../../icons/agg-icon.svg";
import { Contract, TOP_TABS, TopTab } from "../types";
import { ContractCard } from "./ContractCard";
import { AddContractModal, NewContractInput } from "./AddContractModal";
import { DeleteContractModal } from "./DeleteContractModal";
import {
  deleteBankingContract,
  getActiveBankingContracts,
  getInactiveBankingContracts,
} from "../../../../Rest_api/RestApi";
import { BankingContractListItem } from "../../../../models/banking";

export const CUMULATIVE_ID = "__cumulative__";

// Sort LoA numbers so numeric chunks compare as numbers (numbers before
// alphabets), e.g. "02-02/..." sorts before "CGM/PM/...". Falls back to a
// case-insensitive alphabetic compare within the same segment type.
function compareLoa(a: string, b: string): number {
  const collator = new Intl.Collator(undefined, {
    numeric: true,
    sensitivity: "base",
  });
  const startsWithDigit = (s: string) => /^\d/.test(s.trim());
  const aNum = startsWithDigit(a);
  const bNum = startsWithDigit(b);
  // Numbers get first priority over alphabet-leading LoAs.
  if (aNum !== bNum) return aNum ? -1 : 1;
  return collator.compare(a, b);
}

function resolveStatus(
  raw: string | undefined,
  fallback: Contract["status"],
): Contract["status"] {
  const upper = (raw ?? "").toUpperCase();
  if (upper === "ACTIVE" || upper === "INACTIVE") return upper;
  return fallback;
}

function mapApiContract(
  c: BankingContractListItem,
  fallback: Contract["status"],
): Contract {
  return {
    id: c.id,
    from: c.supplier,
    to: c.receiver,
    status: resolveStatus(c.status, fallback),
    loa: c.loa_number,
    bf: `${c.banking_factor_pct}%`,
    signed: c.loa_date,
    windowStart: "",
    windowEnd: "",
    supplyPeriod: c.supply_period_count,
    returnPeriod: c.return_period_count,
    returnWindowStart: "",
    returnWindowEnd: c.return_end,
    returnTimeWindow: "",
    returnDays: 0,
    returnHoursPerDay: 0,
    returnBasis: "Firm",
    traderName: c.traderName ?? "",
  };
}

export function ContractsSidebar({
  topTab,
  setTopTab,
  selectedId,
  setSelectedId,
  search,
  setSearch,
}: {
  topTab: TopTab;
  setTopTab: (t: TopTab) => void;
  selectedId: string;
  setSelectedId: (id: string) => void;
  search: string;
  setSearch: (s: string) => void;
}) {
  const [addOpen, setAddOpen] = useState(false);
  const [apiContracts, setApiContracts] = useState<Contract[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Contract | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const fetchContracts = useCallback(
    async (selectLoaAfter?: string, selectIdAfter?: string) => {
      setLoading(true);
      setLoadError(null);
      try {
        const [active, inactive] = await Promise.all([
          getActiveBankingContracts().then((items) =>
            items.map((c) => mapApiContract(c, "ACTIVE")),
          ),
          getInactiveBankingContracts().then((items) =>
            items.map((c) => mapApiContract(c, "INACTIVE")),
          ),
        ]);
        const all = [...active, ...inactive];
        setApiContracts(all);
        if (selectIdAfter) {
          const found = all.find((c) => c.id === selectIdAfter);
          if (found) { setSelectedId(found.id); return; }
        }
        if (selectLoaAfter) {
          const found = all.find((c) => c.loa === selectLoaAfter);
          if (found) setSelectedId(found.id);
        }
      } catch (err: any) {
        setLoadError(err?.message ?? "Failed to load contracts");
        setApiContracts([]);
      } finally {
        setLoading(false);
      }
    },
    [setSelectedId],
  );

  useEffect(() => {
    void fetchContracts();
  }, [fetchContracts]);

  const allContracts = useMemo(
    () => [...apiContracts],
    [apiContracts],
  );

  useEffect(() => {
    if (selectedId === CUMULATIVE_ID) return;
    if (selectedId) {
      const exists = allContracts.some((c) => c.id === selectedId);
      if (exists) return;
    }
    if (allContracts.length > 0) {
      const firstActive =
        allContracts.find((c) => c.status === "ACTIVE") ?? allContracts[0];
      if (firstActive && firstActive.id !== selectedId) {
        setSelectedId(firstActive.id);
      }
    }
  }, [allContracts, selectedId, setSelectedId]);

  const visibleContracts = useMemo(() => {
    const q = search.trim().toLowerCase();
    return allContracts
      .filter((c) =>
        topTab === "Inactive" ? c.status === "INACTIVE" : c.status === "ACTIVE",
      )
      .filter((c) => {
        if (!q) return true;
        return (
          c.from.toLowerCase().includes(q) ||
          c.to.toLowerCase().includes(q) ||
          c.loa.toLowerCase().includes(q)
        );
      })
      .sort((a, b) => compareLoa(a.loa, b.loa));
  }, [topTab, search, allContracts]);

  const counts = useMemo(
    () => ({
      Active: allContracts.filter((c) => c.status === "ACTIVE").length,
      Inactive: allContracts.filter((c) => c.status === "INACTIVE").length,
    }),
    [allContracts],
  );

  const cumulative = useMemo(() => {
    if (allContracts.length === 0) return null;
    const totalSupply = allContracts.reduce(
      (acc, c) => acc + (c.supplyPeriod || 0),
      0,
    );
    const totalReturn = allContracts.reduce(
      (acc, c) => acc + (c.returnPeriod || 0),
      0,
    );
    const bfValues = allContracts
      .map((c) => parseFloat(c.bf))
      .filter((v) => !Number.isNaN(v));
    const avgBf = bfValues.length
      ? Math.round(bfValues.reduce((a, b) => a + b, 0) / bfValues.length)
      : 0;
    return {
      count: allContracts.length,
      totalSupply,
      totalReturn,
      avgBf,
    };
  }, [allContracts]);

  const showCumulative =
    topTab === "Active" && !search.trim() && cumulative !== null;

  const handleAdd = (input: NewContractInput, savedId?: string) => {
    setAddOpen(false);
    setTopTab("Active");
    void fetchContracts(input.loaNumber || undefined, savedId);
  };

  const closeDeleteModal = () => {
    if (deleting) return;
    setDeleteTarget(null);
    setDeleteError(null);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      // buildHttpResponse resolves (not throws) on HTTP errors, returning
      // { success: false, ... } — so inspect the value rather than rely on catch.
      const res = await deleteBankingContract(deleteTarget.id);
      if (res && res.success === false) {
        const data = res.data;
        const msg =
          (typeof data === "string" ? data : data?.message || data?.detail) ||
          res.message ||
          "Failed to delete contract";
        setDeleteError(msg);
        return;
      }
      if (selectedId === deleteTarget.id) {
        setSelectedId("");
      }
      setDeleteTarget(null);
      await fetchContracts();
    } catch (err: any) {
      setDeleteError(
        typeof err === "string"
          ? err
          : err?.message ?? "Failed to delete contract",
      );
    } finally {
      setDeleting(false);
    }
  };

  return (
    <aside className="bg-white rounded-md border-r border-[#ECEEF3] flex flex-col w-[360px] shrink-0 overflow-y-auto">
      <div className="px-4 pt-4 pb-2 flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-[14px] font-bold text-[#525062]">
              Contracts
            </span>
            <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-[#F0F1F5] text-[#6B6877] text-[11px] font-semibold">
              {allContracts.length}
            </span>
          </div>
          <button
            type="button"
            onClick={() => setAddOpen(true)}
            className="inline-flex items-center gap-1 border border-[#E8EAEF] bg-white rounded-md px-2.5 py-1 text-[12px] text-[#F1935C] font-semibold hover:bg-[#FFF1E6] cursor-pointer"
          >
            <Plus size={13} /> Add
          </button>
        </div>

        <div className="relative">
          <Search
            size={14}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-[#828A99]"
          />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by party, LoA…"
            className="w-full border-none bg-[#F5F6F8] rounded-md h-9 text-xs pl-9 pr-3 outline-none text-[#1A1B2B] placeholder:text-[#A8A6B2] focus:bg-[#EEF0F4]"
          />
        </div>

        <div className="flex gap-4 text-[13px] -mb-1 border-b border-[#F0F1F5]">
          {TOP_TABS.map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setTopTab(t)}
              className={`relative bg-transparent border-none py-1.5 cursor-pointer font-medium px-0 ${
                topTab === t
                  ? "text-[#525062] after:content-[''] after:absolute after:inset-x-0 after:-bottom-[1px] after:h-[2px] after:bg-[#525062] after:rounded-sm"
                  : "text-[#828A99]"
              }`}
            >
              {t}{" "}
              <span
                className={`ml-0.5 font-medium ${
                  topTab === t ? "text-[#6B6877]" : "text-[#A8A6B2]"
                }`}
              >
                {counts[t]}
              </span>
            </button>
          ))}
        </div>
      </div>

      <div className="flex flex-col flex-1 min-h-0">
        {loading && (
          <div className="px-4 py-3 text-[12px] text-[#828A99]">
            Loading contracts…
          </div>
        )}
        {loadError && !loading && (
          <div className="px-4 py-3 text-[12px] text-[#E54848]">
            {loadError}
          </div>
        )}
        {!loading && !loadError && visibleContracts.length === 0 && (
          <div className="flex-1 flex flex-col items-center justify-center px-6 py-10 text-center">
            <div className="w-14 h-14 rounded-full bg-[#FDF1E8] flex items-center justify-center mb-3">
              <FileSearch size={26} className="text-[#E2682B]" />
            </div>
            <div className="text-[13px] font-semibold text-[#525062]">
              No contracts found
            </div>
            <div className="text-[12px] text-[#828A99] mt-1 leading-relaxed">
              {search.trim()
                ? "Try a different search term or clear the filter."
                : `There are no ${topTab.toLowerCase()} contracts yet.`}
            </div>
            {!search.trim() && (
              <button
                type="button"
                onClick={() => setAddOpen(true)}
                className="mt-4 inline-flex items-center gap-1 border border-[#E8EAEF] bg-white rounded-md px-3 py-1.5 text-[12px] text-[#F1935C] font-semibold hover:bg-[#FFF1E6] cursor-pointer"
              >
                <Plus size={13} /> Add contract
              </button>
            )}
          </div>
        )}
        {showCumulative && cumulative && (
          <div className="px-4 py-3">
            <button
              type="button"
              onClick={() => setSelectedId(CUMULATIVE_ID)}
              className={`w-full text-left p-4 rounded-xl border transition-all cursor-pointer flex items-center gap-4 ${
                selectedId === CUMULATIVE_ID
                  ? "bg-[#FEF3EC] border-[#F1935C] border-l-4"
                  : "bg-white border-[#66666640] hover:border-[#F1935C]"
              }`}
            >
              <div className="flex items-center justify-center shrink-0">
                <AggIcon width={25} height={25} />
              </div>
              <div className="flex flex-col">
                <span className="text-[12px] font-semibold text-[#525062]">
                  Aggregate view
                </span>
                <span className="text-[11px] text-[#828A99]">
                  All {cumulative.count} contracts combined
                </span>
              </div>
            </button>
          </div>
        )}
        {visibleContracts.map((c, idx) => (
          <div
            key={c.id}
            className={
              idx !== visibleContracts.length - 1
                ? "border-b border-[#F0F1F5]"
                : ""
            }
          >
            <ContractCard
              contract={c}
              active={c.id === selectedId}
              onClick={() => setSelectedId(c.id)}
              onDelete={() => {
                setDeleteError(null);
                setDeleteTarget(c);
              }}
            />
          </div>
        ))}
      </div>

      <AddContractModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onSubmit={handleAdd}
      />

      <DeleteContractModal
        open={deleteTarget !== null}
        contract={deleteTarget}
        onClose={closeDeleteModal}
        onConfirm={handleConfirmDelete}
        deleting={deleting}
        error={deleteError}
      />
    </aside>
  );
}

export default ContractsSidebar;
